import { create } from 'zustand';
import { spotlightApi, SpotlightItem } from '../api/spotlightApi';

interface SpotlightState {
  credits: number;
  goalLimit: number;
  slotCost: number;
  activeSpotlights: SpotlightItem[];
  currentSpotlightIndex: number;
  loading: boolean;
  error: string | null;
  fetchCredits: () => Promise<void>;
  fetchConfig: () => Promise<void>;
  fetchActiveSpotlights: () => Promise<void>;
  nextSpotlight: () => void;
  purchaseSlot: (ideaId: string, durationHours?: number) => Promise<boolean>;
}

export const useSpotlightStore = create<SpotlightState>((set, get) => ({
  credits: 0,
  goalLimit: 100, // Default fallback
  slotCost: 50, // Default fallback
  activeSpotlights: [],
  currentSpotlightIndex: 0,
  loading: false,
  error: null,

  fetchCredits: async () => {
    try {
      const response = await spotlightApi.getUserCredits();
      if (response.data) {
        set({ credits: response.data.balance, error: null });
      }
    } catch (err: any) {
      set({ error: err.message || 'Failed to fetch credits' });
    }
  },

  fetchConfig: async () => {
    try {
      const response = await spotlightApi.getSpotlightConfig();
      if (response.data) {
        set({ goalLimit: response.data.limit, slotCost: response.data.cost, error: null });
      }
    } catch (err: any) {
      console.error('Failed to fetch spotlight config:', err);
    }
  },

  fetchActiveSpotlights: async () => {
    set({ loading: true, error: null });
    try {
      const response = await spotlightApi.getActiveSpotlights();
      if (response.data?.items) {
        set({ activeSpotlights: response.data.items, loading: false });
      } else {
        set({ activeSpotlights: [], loading: false });
      }
    } catch (err: any) {
      set({ error: err.message || 'Failed to fetch active spotlights', loading: false });
    }
  },

  nextSpotlight: () => {
    const { activeSpotlights, currentSpotlightIndex } = get();
    if (activeSpotlights.length > 0) {
      const nextIndex = (currentSpotlightIndex + 1) % activeSpotlights.length;
      set({ currentSpotlightIndex: nextIndex });
    }
  },

  purchaseSlot: async (ideaId: string, durationHours: number = 24) => {
    set({ loading: true, error: null });
    try {
      const response = await spotlightApi.purchaseSpotlightSlot(ideaId, durationHours);
      if (response.data?.success) {
        // Optimistically update credits and refetch spotlights
        const { credits, slotCost } = get();
        const costMultiplier = durationHours / 24;
        set({ credits: credits - (slotCost * costMultiplier), loading: false });
        get().fetchActiveSpotlights();
        return true;
      }
      set({ loading: false });
      return false;
    } catch (err: any) {
      set({ error: err.message || 'Failed to purchase slot', loading: false });
      return false;
    }
  },
}));
